import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import countryList from "react-select-country-list";
import useMapLocation from "../../hooks/useMapLocation";
import usePath from "../../hooks/usePath";
import { Autocomplete, GoogleMap } from "@react-google-maps/api";
import { useQueryClient } from "@tanstack/react-query";
import {
  useVendorById,
  useAddVendor,
  useUpdateVendor,
  useUploadVendorProfileImage,
  useVendorCategories,
  useMyVendor,
} from "../../hooks/useVendor";
import { useUser } from "../../hooks/useUser";
import axios from "axios";
import apiClient from "../../api/axios";
import SafeImage from "../../components/common/SafeImage";

const AddorUpdate = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: user } = useUser();
  const [vendorInfo, setVendorInfo] = useState({
    firstName: "",
    description: "",
    vendorType: "",
    origin: "",
    address: "",
    email: "",
    phoneNumber: "",
    // profile_image removed
  });
  const [profileImage, setProfileImage] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState("");
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [countries] = useState(countryList().getData());
  const [formLoading, setFormLoading] = useState(false);
  const [formData, setFormData] = useState({
    longitude: "",
    latitude: "",
  });
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [isLocationLoaded, setIsLocationLoaded] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const containerStyle = { width: "100%", height: "300px" };
  const centerDefault = { lat: 20.5937, lng: 78.9629 }; // Center of India, change as needed
  const [mapCenter, setMapCenter] = useState(centerDefault);
  const mapRef = useRef(null);

  const {
    isLoaded,
    country,
    latLng,
    address,
    setAddress,
    setLatLng,
    onPlaceChanged,
    geocodeAddress,
    error: mapError,
    autocompleteRef,
  } = useMapLocation();

  // Fetch categories using hook
  const { data: categoriesResponse, isLoading: categoriesLoading } =
    useVendorCategories();
  const categories = categoriesResponse?.data || [];

  // Fetch vendor data if in edit mode
  let vendorResponse, fetchingVendor;
  if (user && user.role === "vendor" && String(id) === String(user.user_id)) {
    // Vendor editing their own profile
    const myVendor = useMyVendor();
    vendorResponse = myVendor.data;
    fetchingVendor = myVendor.isLoading;
  } else {
    const vendorById = useVendorById(id);
    vendorResponse = vendorById.data;
    fetchingVendor = vendorById.isLoading;
  }
  const vendorData = vendorResponse?.data;

  // Set vendor data when fetched or when id changes
  useEffect(() => {
    if (vendorData) {
      const newVendorInfo = {
        firstName: vendorData.user?.name || "",
        description: vendorData.description || "",
        vendorType:
          (vendorData.vendor_type_id || vendorData.vendor_type)?.toString() ||
          "", // <-- updated mapping
        origin: vendorData.origin || "",
        address: vendorData.address || "",
        email: vendorData.user?.email || "",
        phoneNumber: vendorData.user?.phone_no || "",
        // profile_image removed
      };
      setVendorInfo(newVendorInfo);
      const img = vendorData.user?.profile_image;
      if (img && typeof img === "string" && img.trim() !== "") {
        setProfileImagePreview(
          img.startsWith("http")
            ? img
            : (import.meta.env.VITE_API_URL || "http://localhost:8000") + img,
        );
      }
      // Pre-fill map and formData with latitude and longitude from database
      if (
        (vendorData.latitude || vendorData.latitude === 0) &&
        (vendorData.longitude || vendorData.longitude === 0)
      ) {
        setFormData({
          latitude: vendorData.latitude.toString(),
          longitude: vendorData.longitude.toString(),
        });
        setMapCenter({
          lat: parseFloat(vendorData.latitude),
          lng: parseFloat(vendorData.longitude),
        });
        setLatLng({
          lat: parseFloat(vendorData.latitude),
          lng: parseFloat(vendorData.longitude),
        });
      }
    }
  }, [vendorData]);

  //get vendor data
  useEffect(() => {
    if (!id) {
      setVendorInfo({
        firstName: "",
        description: "",
        vendorType: "",
        origin: "",
        address: "",
        email: "",
        phoneNumber: "",
        // profile_image removed
      });
      setProfileImage(null);
      setProfileImagePreview("");
    }
  }, [id]);

  // Mutations for adding/updating vendors
  const addVendorMutation = useAddVendor({
    onSuccess: (result) => {
      if (result.success === true || result.status === "success") {
        toast.success(result.message || "Vendor added successfully");
        queryClient.invalidateQueries(["vendors"]);
        navigate("/vendor");
      } else {
        toast.error(result.message || "Failed to add vendor");
      }
    },
    onError: (error) => {
      // Show backend validation errors in the form
      const backendErrors = error?.response?.data?.errors;
      let firstErrorMsg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to add vendor";
      if (backendErrors) {
        setErrors((prev) => ({
          ...prev,
          ...Object.fromEntries(
            Object.entries(backendErrors).map(([key, value]) => [
              key === "name"
                ? "firstName"
                : key === "phone_no"
                  ? "phoneNumber"
                  : key, // add more mappings as needed
              Array.isArray(value) ? value[0] : value,
            ]),
          ),
        }));
        // Show the first backend error in the toast
        const firstField = Object.keys(backendErrors)[0];
        const firstMsg = Array.isArray(backendErrors[firstField])
          ? backendErrors[firstField][0]
          : backendErrors[firstField];
        firstErrorMsg = firstMsg || firstErrorMsg;
      }
      toast.error(firstErrorMsg);
    },
    onSettled: () => setFormLoading(false),
  });

  const updateVendorMutation = useUpdateVendor({
    onSuccess: (result) => {
      if (result.success === true || result.status === "success") {
        toast.success(result.message || "Vendor updated successfully");
        queryClient.invalidateQueries(["vendor", id]);
        queryClient.invalidateQueries(["vendors"]);
        navigate("/vendor");
      } else {
        toast.error(result.message || "Failed to update vendor");
      }
    },
    onError: (error) => {
      // Show backend validation errors in the form
      const backendErrors = error?.response?.data?.errors;
      let firstErrorMsg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update vendor";
      if (backendErrors) {
        setErrors((prev) => ({
          ...prev,
          ...Object.fromEntries(
            Object.entries(backendErrors).map(([key, value]) => [
              key === "name"
                ? "firstName"
                : key === "phone_no"
                  ? "phoneNumber"
                  : key, // add more mappings as needed
              Array.isArray(value) ? value[0] : value,
            ]),
          ),
        }));
        // Show the first backend error in the toast
        const firstField = Object.keys(backendErrors)[0];
        const firstMsg = Array.isArray(backendErrors[firstField])
          ? backendErrors[firstField][0]
          : backendErrors[firstField];
        firstErrorMsg = firstMsg || firstErrorMsg;
      }
      toast.error(firstErrorMsg);
    },
    onSettled: () => setFormLoading(false),
  });

  const uploadImageMutation = useUploadVendorProfileImage({
    isVendor: false, // Admin/employee updating vendor profile image
    onSuccess: (result) => {
      if (result.success === true || result.status === "success") {
        queryClient.invalidateQueries(["vendor", id]);
        toast.success("Profile image updated successfully");
      } else {
        toast.error(result.message || "Failed to update profile image");
      }
    },
    onError: (error) => {
      toast.error(
        "Failed to update profile image: " + (error.message || "Unknown error"),
      );
    },
    onSettled: () => setFormLoading(false),
  });

  const handleInfoChange = (e) => {
    const { name, value } = e.target;
    setVendorInfo((prevInfo) => ({
      ...prevInfo,
      [name]: value,
    }));
  };

  const handlePhoneChange = (value) => {
    setVendorInfo((prevInfo) => ({
      ...prevInfo,
      phoneNumber: value,
    }));
  };

  const handleProfileImageChange = (e) => {
    const file = e.target.files[0];
    console.log("Selected file:", file);
    if (file) {
      setProfileImage(file);
      setProfileImagePreview(URL.createObjectURL(file));
    }
  };

  const handleFileChange = (e) => {
    setProfileImage(e.target.files[0]);
  };

  const handleLocationSelect = () => {
    if (mapCenter) {
      setFormData((prev) => ({
        ...prev,
        longitude: mapCenter.lng.toString(),
        latitude: mapCenter.lat.toString(),
      }));
      // Reverse geocode to update address
      if (window.google && window.google.maps) {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: mapCenter }, (results, status) => {
          if (status === "OK" && results[0]) {
            setAddress(results[0].formatted_address);
            setVendorInfo((prev) => ({
              ...prev,
              address: results[0].formatted_address,
            }));
          }
        });
      }
      setShowLocationModal(false);
      setIsLocationLoaded(true);
      if (errors.location) {
        setErrors((prev) => ({ ...prev, location: "" }));
      }
    }
  };

  const handleOpenLocationModal = () => {
    setShowLocationModal(true);
    setTouched((prev) => ({ ...prev, location: true }));
    if (!latLng) {
      setLatLng(centerDefault);
    }
  };

  const handleGetCurrentLocation = () => {
    setLocationLoading(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setLatLng({ lat: latitude, lng: longitude });
          // Reverse geocode to get address
          if (window.google && window.google.maps) {
            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode(
              { location: { lat: latitude, lng: longitude } },
              (results, status) => {
                if (status === "OK" && results[0]) {
                  setAddress(results[0].formatted_address);
                }
                setLocationLoading(false);
              },
            );
          } else {
            setLocationLoading(false);
          }
        },
        (error) => {
          toast.error(
            "Unable to fetch your location. Please allow location access.",
          );
          setLocationLoading(false);
        },
      );
    } else {
      toast.error("Geolocation is not supported by your browser.");
      setLocationLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!vendorInfo.firstName) newErrors.firstName = "Shop name is required";
    if (!vendorInfo.vendorType)
      newErrors.vendorType = "Vendor type is required";
    if (!vendorInfo.origin) newErrors.origin = "Origin is required";
    if (!vendorInfo.address) newErrors.address = "Address is required";
    if (!vendorInfo.email) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(vendorInfo.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    if (!vendorInfo.phoneNumber) {
      newErrors.phoneNumber = "Phone number is required";
    } else if (!/^\+?[1-9]\d{7,14}$/.test(vendorInfo.phoneNumber)) {
      newErrors.phoneNumber = "Please enter a valid international phone number";
    }
    if (!formData.longitude || !formData.latitude) {
      newErrors.location = "Business location is required";
    }
    return newErrors;
  };

  useEffect(() => {
    setErrors(validateForm());
  }, [vendorInfo, formData]); // Removed 'errors' from dependencies

  useEffect(() => {
    if (latLng) {
      setMapCenter(latLng);
    }
  }, [latLng]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({
      firstName: true,
      vendorType: true,
      origin: true,
      address: true,
      email: true,
      phoneNumber: true,
      location: true,
    });
    const formErrors = validateForm();
    if (Object.values(formErrors).length > 0) {
      // Only show a toast for client-side validation errors
      toast.error("Please fix the errors in the form.");
      return;
    }
    setFormLoading(true);
    const payload = {
      name: vendorInfo.firstName,
      email: vendorInfo.email,
      phone_no: vendorInfo.phoneNumber,
      vendor_type: vendorInfo.vendorType,
      origin: vendorInfo.origin,
      address: vendorInfo.address,
      description: vendorInfo.description,
      longitude: formData.longitude,
      latitude: formData.latitude,
    };
    // Check if this is an update operation first
    if (id) {
      // Update existing vendor
      updateVendorMutation.mutate({ id, payload });
      if (profileImage && profileImage.name && profileImage.size) {
        uploadImageMutation.mutate({ id, file: profileImage });
      }
      return;
    }

    // Add new vendor - check if admin/employee or regular user
    if (user?.role === "admin" || user?.role === "employee") {
      const formData = new FormData();
      formData.append("name", payload.name);
      formData.append("email", payload.email);
      formData.append("phone_no", payload.phone_no);
      formData.append("vendor_type", payload.vendor_type);
      formData.append("origin", payload.origin);
      formData.append("address", payload.address);
      formData.append("description", payload.description);
      formData.append("longitude", payload.longitude);
      formData.append("latitude", payload.latitude);
      if (profileImage) {
        formData.append("profile_image", profileImage);
      }
      try {
        const response = await apiClient.post(
          usePath("/api/admin/vendors/register"),
          formData,
        );
        toast.success("Vendor and image added successfully");
        queryClient.invalidateQueries(["vendors"]);
        navigate("/vendor");
        setFormLoading(false);
      } catch (error) {
        setFormLoading(false);
        // Show backend validation errors in the form
        const backendErrors = error?.response?.data?.errors;
        let firstErrorMsg =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to add vendor";
        if (backendErrors) {
          setErrors((prev) => ({
            ...prev,
            ...Object.fromEntries(
              Object.entries(backendErrors).map(([key, value]) => [
                key === "name"
                  ? "firstName"
                  : key === "phone_no"
                    ? "phoneNumber"
                    : key, // add more mappings as needed
                Array.isArray(value) ? value[0] : value,
              ]),
            ),
          }));
          // Show the first backend error in the toast
          const firstField = Object.keys(backendErrors)[0];
          const firstMsg = Array.isArray(backendErrors[firstField])
            ? backendErrors[firstField][0]
            : backendErrors[firstField];
          firstErrorMsg = firstMsg || firstErrorMsg;
        }
        toast.error(firstErrorMsg);
      }
      return;
    }
    if (!id) {
      // Two-step: 1. Register vendor, 2. Upload image if present
      let formDataObj = new FormData();
      Object.entries(payload).forEach(([key, value]) => {
        formDataObj.append(key, value);
      });
      // Remove image from registration step

      addVendorMutation.mutate(formDataObj, {
        onSuccess: (result) => {
          if (
            (result.success === true || result.status === "success") &&
            result.data &&
            result.data.vendor &&
            profileImage &&
            profileImage.name &&
            profileImage.size
          ) {
            // Step 2: Upload image
            uploadImageMutation.mutate(
              { id: result.data.vendor.vendor_id, file: profileImage },
              {
                onSuccess: (imgResult) => {
                  toast.success("Vendor and image added successfully");
                  queryClient.invalidateQueries(["vendors"]);
                  navigate("/vendor");
                },
                onError: (imgError) => {
                  toast.error("Vendor added, but failed to upload image");
                  queryClient.invalidateQueries(["vendors"]);
                  navigate("/vendor");
                },
                onSettled: () => setFormLoading(false),
              },
            );
          } else if (result.success === true || result.status === "success") {
            toast.success(result.message || "Vendor added successfully");
            queryClient.invalidateQueries(["vendors"]);
            navigate("/vendor");
            setFormLoading(false);
          } else {
            toast.error(result.message || "Failed to add vendor");
            setFormLoading(false);
          }
        },
        // Do not show toast here for backend errors, only in onError below
        onError: (error) => {
          // Show backend validation errors in the form
          const backendErrors = error?.response?.data?.errors;
          let firstErrorMsg =
            error?.response?.data?.message ||
            error?.message ||
            "Failed to add vendor";
          if (backendErrors) {
            setErrors((prev) => ({
              ...prev,
              ...Object.fromEntries(
                Object.entries(backendErrors).map(([key, value]) => [
                  key === "name"
                    ? "firstName"
                    : key === "phone_no"
                      ? "phoneNumber"
                      : key, // add more mappings as needed
                  Array.isArray(value) ? value[0] : value,
                ]),
              ),
            }));
            // Show the first backend error in the toast
            const firstField = Object.keys(backendErrors)[0];
            const firstMsg = Array.isArray(backendErrors[firstField])
              ? backendErrors[firstField][0]
              : backendErrors[firstField];
            firstErrorMsg = firstMsg || firstErrorMsg;
          }
          toast.error(firstErrorMsg);
          setFormLoading(false);
        },
        onSettled: () => {
          // Don't setFormLoading(false) here, handled above
        },
      });
    }
  };

  const isEdit = !!id;

  if (fetchingVendor) {
    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body text-center">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading vendor data...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Location Modal - Clean Bootstrap Design */}
      {showLocationModal && isLoaded && (
        <>
          <div
            className="modal-backdrop fade show"
            onClick={() => setShowLocationModal(false)}
          ></div>
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            aria-labelledby="locationModalLabel"
            aria-modal="true"
            style={{ background: "rgba(0,0,0,0.1)" }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <div>
                    <h5 className="modal-title" id="locationModalLabel">
                      Choose your Business Location
                    </h5>
                    <h6 className="mb-0 text-muted">{country}</h6>
                    <p
                      className="mt-1 text-muted mb-0"
                      style={{ fontSize: "0.95rem" }}
                    >
                      Enter your address and we will specify the offer for your
                      area.
                    </p>
                  </div>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <div className="input-group">
                      <span
                        className="input-group-text bg-white border-end-0"
                        style={{ paddingRight: 0 }}
                      >
                        <i className="fa fa-search text-muted"></i>
                      </span>
                      {isLoaded && (
                        <Autocomplete
                          onLoad={(ref) => (autocompleteRef.current = ref)}
                          onPlaceChanged={onPlaceChanged}
                        >
                          <input
                            type="text"
                            placeholder="Search address"
                            className="form-control border-start-0"
                            style={{ maxWidth: 300 }}
                            value={address || ""}
                            onChange={(e) => setAddress(e.target.value)}
                          />
                        </Autocomplete>
                      )}
                      <button
                        onClick={handleLocationSelect}
                        className="btn btn-primary ms-2 d-flex align-items-center gap-2"
                        disabled={!mapCenter}
                        type="button"
                      >
                        <i className="fas fa-map-marker-alt"></i>
                        Set Location
                      </button>
                      {locationLoading && (
                        <div style={{ textAlign: "center", margin: "1rem 0" }}>
                          <div className="spinner-border" role="status" />
                          <div>Fetching your current location...</div>
                        </div>
                      )}
                      <button
                        type="button"
                        className="btn ms-2 d-flex align-items-center gap-2"
                        onClick={handleGetCurrentLocation}
                        style={{ minWidth: 140 }}
                        disabled={locationLoading}
                      >
                        <i
                          className="fa fa-location-arrow me-1"
                          style={{ opacity: 0.7 }}
                        ></i>
                        {locationLoading
                          ? "Locating..."
                          : "Get Current Location"}
                      </button>
                    </div>
                  </div>
                  {address && (
                    <div className="mb-2">
                      <p className="mb-0">
                        <strong>Address:</strong> {address}
                      </p>
                    </div>
                  )}
                  {mapError && (
                    <p className="text-danger small mb-2">{mapError}</p>
                  )}
                  <div className="mb-2">
                    <div
                      style={{
                        position: "relative",
                        width: "100%",
                        height: 300,
                      }}
                    >
                      <GoogleMap
                        mapContainerStyle={containerStyle}
                        center={mapCenter}
                        zoom={latLng ? 15 : 6}
                        onLoad={(map) => (mapRef.current = map)}
                        onDragEnd={() => {
                          if (mapRef.current) {
                            const newCenter = mapRef.current.getCenter();
                            setMapCenter({
                              lat: newCenter.lat(),
                              lng: newCenter.lng(),
                            });
                          }
                        }}
                      />
                      {/* Fixed pin overlay */}
                      <div
                        style={{
                          position: "absolute",
                          left: "50%",
                          top: "50%",
                          transform: "translate(-50%, -100%)",
                          pointerEvents: "none",
                          zIndex: 2,
                        }}
                      >
                        <i
                          className="fas fa-map-marker-alt"
                          style={{ fontSize: 36, color: "#d00" }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowLocationModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleLocationSelect}
                    disabled={!mapCenter}
                  >
                    Save Location
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="mb-4">
              <h2 className="fw-bold">
                {isEdit ? "Edit Vendor" : "Add New Vendor"}
              </h2>
            </div>

            <form
              className="theme-form theme-form-2 mega-form"
              onSubmit={handleSubmit}
            >
              <div className="card-header-1"></div>
              <div className="row">
                <div className="mb-4 row align-items-center">
                  <label className="col-lg-10 col-md-1 col-form-label form-label-title">
                    Shop Name <span className="text-danger">*</span>
                  </label>
                  <div className="col-md-9 col-lg-10">
                    <input
                      className="form-control"
                      type="text"
                      name="firstName"
                      value={vendorInfo.firstName}
                      onChange={handleInfoChange}
                      onBlur={() =>
                        setTouched((prev) => ({
                          ...prev,
                          firstName: true,
                        }))
                      }
                      required
                    />
                    {touched.firstName && errors.firstName && (
                      <div className="text-danger small mt-1">
                        {errors.firstName}
                      </div>
                    )}
                  </div>
                </div>

                <div className="mb-4 row align-items-center">
                  <label className="col-lg-10 col-md-1 col-form-label form-label-title">
                    Description
                  </label>
                  <div className="col-md-9 col-lg-10">
                    <input
                      className="form-control"
                      type="text"
                      name="description"
                      value={vendorInfo.description}
                      onChange={handleInfoChange}
                    />
                  </div>
                </div>

                <div className="mb-4 row align-items-center">
                  <label className="col-lg-10 col-md-1 col-form-label form-label-title">
                    Vendor Type <span className="text-danger">*</span>
                  </label>
                  <div className="col-md-9 col-lg-10">
                    {categoriesLoading ? (
                      <div className="form-control">Loading categories...</div>
                    ) : (
                      <>
                        <select
                          className="form-select"
                          name="vendorType"
                          value={vendorInfo.vendorType}
                          onChange={handleInfoChange}
                          onBlur={() =>
                            setTouched((prev) => ({
                              ...prev,
                              vendorType: true,
                            }))
                          }
                          required
                        >
                          <option value="">Select Vendor Type</option>
                          {categories?.map((cat) => (
                            <option
                              key={cat.category_id}
                              value={cat.category_id}
                            >
                              {cat.category_name}
                            </option>
                          ))}
                        </select>
                        {touched.vendorType && errors.vendorType && (
                          <div className="text-danger small mt-1">
                            {errors.vendorType}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                <div className="mb-4 row align-items-center">
                  <label className="col-lg-10 col-md-1 col-form-label form-label-title">
                    Origin <span className="text-danger">*</span>
                  </label>
                  <div className="col-md-9 col-lg-10">
                    <select
                      className="form-select"
                      name="origin"
                      value={vendorInfo.origin}
                      onChange={handleInfoChange}
                      onBlur={() =>
                        setTouched((prev) => ({
                          ...prev,
                          origin: true,
                        }))
                      }
                      required
                    >
                      <option value="" disabled>
                        Select Country
                      </option>
                      {countries?.map((country) => (
                        <option key={country.value} value={country.label}>
                          {country.label}
                        </option>
                      ))}
                    </select>
                    {touched.origin && errors.origin && (
                      <div className="text-danger small mt-1">
                        {errors.origin}
                      </div>
                    )}
                  </div>
                </div>

                <div className="mb-4 row align-items-center" hidden>
                  <label className="form-label-title col-lg-2 col-md-3 mb-0">
                    Address <span className="text-danger">*</span>
                  </label>
                  <div className="col-md-9 col-lg-10">
                    <input
                      className="form-control"
                      type="text"
                      name="address"
                      value={vendorInfo.address}
                      onChange={handleInfoChange}
                      onBlur={() =>
                        setTouched((prev) => ({
                          ...prev,
                          address: true,
                        }))
                      }
                      required
                    />
                    {touched.address && errors.address && (
                      <div className="text-danger small mt-1">
                        {errors.address}
                      </div>
                    )}
                  </div>
                </div>

                <div className="mb-4 row align-items-center">
                  <label className="col-lg-10 col-md-1 col-form-label form-label-title">
                    Business Location <span className="text-danger">*</span>
                  </label>
                  <div className="col-md-9 col-lg-10">
                    <div
                      className={`form-control text-start${
                        touched.location && errors.location ? " is-invalid" : ""
                      }`}
                      style={{
                        backgroundColor: "white",
                        cursor: formLoading ? "not-allowed" : "pointer",
                        minHeight: "45px",
                        display: "flex",
                        alignItems: "center",
                        padding: "0 12px",
                      }}
                      tabIndex={0}
                      onClick={handleOpenLocationModal}
                      role="button"
                      aria-disabled={formLoading}
                    >
                      <i className="fa fa-map-marker-alt text-primary me-2"></i>
                      <span
                        className={
                          formData.longitude && formData.latitude
                            ? "text-dark small"
                            : "text-muted small"
                        }
                        style={{ fontWeight: 400 }}
                      >
                        {formData.longitude && formData.latitude
                          ? "Location Selected"
                          : "Click to select your business location"}
                      </span>
                    </div>
                    {touched.location && errors.location && (
                      <div className="invalid-feedback d-block">
                        {errors.location}
                      </div>
                    )}
                  </div>
                </div>

                <div className="mb-4 row align-items-center">
                  <label className="col-lg-10 col-md-1 col-form-label form-label-title">
                    Email Address <span className="text-danger">*</span>
                  </label>
                  <div className="col-md-9 col-lg-10">
                    <input
                      className="form-control"
                      type="email"
                      name="email"
                      value={vendorInfo.email}
                      onChange={handleInfoChange}
                      onBlur={() =>
                        setTouched((prev) => ({ ...prev, email: true }))
                      }
                      required
                    />
                    {touched.email && errors.email && (
                      <div className="text-danger small mt-1">
                        {errors.email}
                      </div>
                    )}
                  </div>
                </div>

                <div className="mb-4 row align-items-center">
                  <label className="col-lg-10 col-md-1 col-form-label form-label-title">
                    Phone Number <span className="text-danger">*</span>
                  </label>
                  <div className="col-md-9 col-lg-12 position-relative">
                    <PhoneInput
                      country={"us"}
                      value={vendorInfo.phoneNumber}
                      onChange={handlePhoneChange}
                      inputProps={{
                        name: "phoneNumber",
                        required: true,
                        className: "form-control custom-phone-input",
                        onBlur: () =>
                          setTouched((prev) => ({
                            ...prev,
                            phoneNumber: true,
                          })),
                      }}
                      containerClass="w-100"
                      inputClass="w-100"
                      buttonClass="custom-phone-button"
                    />
                    {touched.phoneNumber && errors.phoneNumber && (
                      <div className="text-danger small mt-1">
                        {errors.phoneNumber}
                      </div>
                    )}
                  </div>
                </div>

                <div className="mb-4 row align-items-center">
                  <label className="col-lg-2 col-md-3 col-form-label form-label-title">
                    Profile Image
                  </label>
                  <div className="col-md-9 col-lg-10 d-flex align-items-center">
                    <input
                      type="file"
                      name="profile_image"
                      accept="image/*"
                      className="form-control"
                      onChange={handleProfileImageChange}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    />
                    <SafeImage
                      src={profileImagePreview || "/avatar-placeholder.png"}
                      alt="Profile Preview"
                      style={{
                        width: 60,
                        height: 60,
                        objectFit: "cover",
                        borderRadius: "50%",
                        marginLeft: 16,
                      }}
                      fallbackSrc="/avatar-placeholder.png"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-5 mb-4">
                <button
                  className="btn btn-animation"
                  type="submit"
                  disabled={formLoading || Object.values(errors).length > 0}
                  style={{
                    backgroundColor:
                      formLoading || Object.values(errors).length > 0
                        ? "#6c757d"
                        : "",
                    color:
                      formLoading || Object.values(errors).length > 0
                        ? "#ffffff"
                        : "",
                    cursor:
                      formLoading || Object.values(errors).length > 0
                        ? "not-allowed"
                        : "pointer",
                    opacity:
                      formLoading || Object.values(errors).length > 0 ? 0.6 : 1,
                  }}
                >
                  {formLoading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Saving...
                    </>
                  ) : isEdit ? (
                    "Update"
                  ) : (
                    "Submit"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddorUpdate;
